import {
  CUSTOM_SIZE,
  FONTS,
  PLACARD_COLORS,
  TEXT_RULES,
  VINYL_COLORS,
} from './constants.js';
import { maxLinesFor } from './layout.js';
import type { PatchConfig, Size, TextLine, ValidationError, ValidationResult } from './types.js';

function err(field: string, code: string, message: string): ValidationError {
  return { field, code, message };
}

function isMultipleOf(value: number, step: number): boolean {
  const scaled = Math.round(value / step);
  return Math.abs(scaled * step - value) < 1e-9;
}

export function validateSize(size: Size): ValidationError[] {
  const errors: ValidationError[] = [];
  if (
    typeof size.width !== 'number' ||
    typeof size.height !== 'number' ||
    !Number.isFinite(size.width) ||
    !Number.isFinite(size.height)
  ) {
    errors.push(err('size', 'size.not_finite', 'Width and height must be finite numbers'));
    return errors;
  }
  if (size.width < CUSTOM_SIZE.minInches || size.height < CUSTOM_SIZE.minInches) {
    errors.push(
      err('size', 'size.too_small', `Minimum dimension is ${CUSTOM_SIZE.minInches}"`),
    );
  }
  if (size.width > CUSTOM_SIZE.maxInches || size.height > CUSTOM_SIZE.maxInches) {
    errors.push(
      err('size', 'size.too_large', `Maximum dimension is ${CUSTOM_SIZE.maxInches}"`),
    );
  }
  if (
    !isMultipleOf(size.width, CUSTOM_SIZE.incrementInches) ||
    !isMultipleOf(size.height, CUSTOM_SIZE.incrementInches)
  ) {
    errors.push(
      err(
        'size',
        'size.invalid_increment',
        `Dimensions must be in ${CUSTOM_SIZE.incrementInches}" increments`,
      ),
    );
  }
  return errors;
}

export function validateTextLines(lines: readonly TextLine[]): ValidationError[] {
  const errors: ValidationError[] = [];
  if (!Array.isArray(lines) || lines.length < TEXT_RULES.minLines || lines.length > TEXT_RULES.maxLines) {
    errors.push(
      err('lines', 'lines.count', `Must have between ${TEXT_RULES.minLines} and ${TEXT_RULES.maxLines} lines`),
    );
    return errors;
  }
  lines.forEach((line, i) => {
    if (typeof line.text !== 'string') {
      errors.push(err(`lines.${i}.text`, 'line.text.type', 'Text must be a string'));
      return;
    }
    if (line.text !== line.text.toUpperCase()) {
      errors.push(err(`lines.${i}.text`, 'line.text.not_uppercase', 'Text must be uppercase'));
    }
    if (line.text.length === 0) {
      errors.push(err(`lines.${i}.text`, 'line.text.empty', 'Line cannot be empty'));
    }
    if (line.text.length > TEXT_RULES.maxCharsPerLine) {
      errors.push(
        err(
          `lines.${i}.text`,
          'line.text.too_long',
          `Max ${TEXT_RULES.maxCharsPerLine} characters per line`,
        ),
      );
    }
    if (!TEXT_RULES.allowedChars.test(line.text)) {
      errors.push(err(`lines.${i}.text`, 'line.text.bad_chars', 'Contains disallowed characters'));
    }
    if (line.size !== 'small' && line.size !== 'large') {
      errors.push(err(`lines.${i}.size`, 'line.size.invalid', 'Size must be "small" or "large"'));
    }
  });
  return errors;
}

export function validateConfig(config: PatchConfig): ValidationResult {
  const errors: ValidationError[] = [];

  errors.push(...validateSize(config.size));
  errors.push(...validateTextLines(config.lines));

  const maxLines = maxLinesFor(config.size);
  if (config.lines.length > maxLines) {
    errors.push(
      err('lines', 'lines.too_many_for_size',
        maxLines === 1
          ? '1" panels are single-line only'
          : `Maximum ${maxLines} lines for this size`),
    );
  }

  if (!FONTS.some((f) => f.id === config.font)) {
    errors.push(err('font', 'font.unknown', 'Font is not in the curated list'));
  }
  if (!VINYL_COLORS.some((c) => c.id === config.vinylColorId)) {
    errors.push(err('vinylColorId', 'vinylColor.unknown', 'Vinyl color is not in the curated list'));
  }
  if (!PLACARD_COLORS.some((c) => c.id === config.placardColorId)) {
    errors.push(
      err('placardColorId', 'placardColor.unknown', 'Placard color is not in the curated list'),
    );
  }
  if (!Number.isInteger(config.quantity) || config.quantity < 1) {
    errors.push(err('quantity', 'quantity.invalid', 'Quantity must be a positive integer'));
  }
  if (config.quantity > 10_000) {
    errors.push(err('quantity', 'quantity.too_large', 'Quantity exceeds maximum of 10,000'));
  }

  return errors.length === 0 ? { ok: true } : { ok: false, errors };
}
